import { DestroyRef, inject, Injectable } from '@angular/core';
import { UdlControlledVocabulary } from '../../shared/models/udl-controlled-vocabulary.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, firstValueFrom, map, Observable, take } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

@Injectable({
    providedIn: 'root'
})

export class UdlControlledVocabularyService {
    private destroyRef = inject(DestroyRef)
    private loaded = new BehaviorSubject<boolean>(false);
    public loaded$ = this.loaded.asObservable();
    private loading: boolean = false;

    private UdlAdministratorKey = 'UDLAdministrator';
    private PCTSampleFileKey = 'PCTSampleFile';
    private OnOffConsiderationsSampleFileKey = 'OnOffConsiderationsSampleFile';
    private IndirectRelationshipsSampleFileKey = 'IndirectRelationshipsSampleFile';

    public speciesDistributionOptions: UdlControlledVocabulary[] = [];
    public regionsToIncludeOptions: UdlControlledVocabulary[] = [];
    public statesToIncludeOptions: UdlControlledVocabulary[] = [];
    public onOffFieldConsiderationsOptions: UdlControlledVocabulary[] = [];
    public bufferDistancesOptions: UdlControlledVocabulary[] = [];
    public udlAdministrators: string = '';
    public pctSampleFileName: string = "";
    public onOffConsiderationsSampleFileName: string = "";
    public indirectRelationshipsSampleFileName: string = "";

    constructor(private http: HttpClient) {
        async () => await this.WaitTillLoaded();
    }

    public async WaitTillLoaded() {
        await this.loadVocabularyValues();
        while (!this.loaded.value) {
            await new Promise(resolve => setTimeout(resolve, 500));
        }
    }

    /**
    * Gets items from udl_controlled_vocabulary table based on the key specified
    * @param key unique key name
    * @returns list of ControlledVocabularyItems that matches with the key
    */
    public getControlledVocabularyItemsByKey(key: string): Observable<UdlControlledVocabulary[]> {
        const apiUrl = `${environment.BAMS_Api_URL}api/ControlledVocabulary/${key}`;
        return this.http.get(apiUrl).pipe(map((data: any) => data.Value));
    }

    public async loadVocabularyValues() {
        if (this.loaded.value || this.loading) return;
        this.loading = true;
        await Promise.all([
            await firstValueFrom(this.getControlledVocabularyItemsByKey("SpeciesHabitat")),
            await firstValueFrom(this.getControlledVocabularyItemsByKey("Regions")),
            await firstValueFrom(this.getControlledVocabularyItemsByKey("States")),
            await firstValueFrom(this.getControlledVocabularyItemsByKey("OnOffFieldConsiderations")),
            await firstValueFrom(this.getControlledVocabularyItemsByKey("BufferDistances")),
            await firstValueFrom(this.getControlledVocabularyItemsByKey(this.UdlAdministratorKey)),
            await firstValueFrom(this.getControlledVocabularyItemsByKey(this.PCTSampleFileKey)),
            await firstValueFrom(this.getControlledVocabularyItemsByKey(this.OnOffConsiderationsSampleFileKey)),
            await firstValueFrom(this.getControlledVocabularyItemsByKey(this.IndirectRelationshipsSampleFileKey))
        ]).then(([speciesHabitat, regions, states, onOffFieldConsiderations, bufferDistances, udlAdministrators, pctSampleFileName, onOffConsiderationsSampleFileName, indirectRelationshipsSampleFileName]) => {
            this.speciesDistributionOptions = speciesHabitat;
            this.regionsToIncludeOptions = regions;
            this.statesToIncludeOptions = states;
            this.onOffFieldConsiderationsOptions = onOffFieldConsiderations;
            this.bufferDistancesOptions = bufferDistances;

            this.udlAdministrators = udlAdministrators.map(item => item?.long_value).filter(value => value).join('; ') || '';
            this.pctSampleFileName = pctSampleFileName[0]?.long_value || '';
            this.onOffConsiderationsSampleFileName = onOffConsiderationsSampleFileName[0]?.long_value || '';
            this.indirectRelationshipsSampleFileName = indirectRelationshipsSampleFileName[0]?.long_value || '';

            this.loading = false;
            this.loaded.next(true);
        });
    }
}